import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Remover importações do Firebase Auth
// import { auth } from '../firebase/config';
// import { onAuthStateChanged } from 'firebase/auth';

// Importar nosso serviço de API
import { apiService } from '../services/ApiService';
// Importar o apiLoader para carregar dados iniciais após o login

// Lista de domínios de email públicos
const dominiosPublicos = [
  '@gmail.com', '@outlook.com', '@hotmail.com', '@live.com', '@msn.com',
  '@yahoo.com', '@yahoo.com.br', '@icloud.com', '@me.com', '@mac.com',
  '@zoho.com', '@protonmail.com', '@tutanota.com', '@uol.com.br',
  '@bol.com.br', '@terra.com.br', '@ig.com.br', '@globo.com', '@r7.com',
  '@oi.com.br', '@gmx.com', '@mail.com', '@aol.com', '@yandex.com',
  '@fastmail.com', '@startmail.com', '@mailbox.org', '@posteo.de',
  '@runbox.com', '@criptext.com', '@example.com'
];

// Função para extrair o domínio do email
function extrairDominio(email) {
  if (!email) return null;
  const indexAt = email.indexOf('@');
  return indexAt !== -1 ? email.substring(indexAt) : null;
}

// Função para gerar um hash baseado no timestamp atual
function gerarHashAleatorio() {
  const timestamp = Date.now();
  const randomNum = Math.floor(Math.random() * 1000000);
  return `user_${timestamp.toString(36)}_${randomNum.toString(36)}`;
}

// Função para extrair o nome da empresa do email ou gerar hash para emails públicos
function extrairNomeEmpresa(email) {
  if (!email) return null;
  
  // Extrair o domínio do email
  const dominio = extrairDominio(email);
  if (!dominio) return null;
  
  // Verificar se é um domínio público
  const ehDominioPublico = dominiosPublicos.some(d => 
    dominio.toLowerCase() === d.toLowerCase()
  );
  
  if (ehDominioPublico) {
    // Para emails públicos, gerar um hash aleatório
    return gerarHashAleatorio();
  } else {
    // Para emails corporativos, extrair nome da empresa
    const match = email.match(/@([^.]+)\./);
    return match ? match[1] : null;
  }
}

// Função para normalizar empresa
const normalizeEmpresa = (empresa) => {
  if (!empresa) return '';
  return empresa.trim().toLowerCase();
};

// Criando o contexto
const AuthContext = createContext();

// Hook para usar o contexto
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provedor do contexto
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Função para verificar role do usuário
  const checkUserRole = (user) => {
    if (!user) return false;
    if (user.role === 'inativo') {
      
      apiService.logout();
      setCurrentUser(null);
      navigate('/login', { replace: true });
      return false;
    }
    return true;
  };

  // Effect para verificar se já existe um usuário autenticado
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Verificar se temos um token e dados do usuário no localStorage
        if (apiService.isAuthenticated()) {
          const user = apiService.getCurrentUser();
          
          if (user) {
            // Tentar extrair a empresa do email
            if (user.email) {
              const empresaDoEmail = extrairNomeEmpresa(user.email);
              if (empresaDoEmail) {
                user.empresa = normalizeEmpresa(empresaDoEmail);
              } else if (user.empresa) {
                // Se não conseguir extrair do email, usar a empresa já definida
                user.empresa = normalizeEmpresa(user.empresa);
              }
            }

            // Verificar se o tipoPlano está presente, caso contrário tentar buscá-lo do localStorage
            if (!user.tipoPlano) {
              try {
                // Verificar em várias possíveis fontes
                // 1. Diretamente do localStorage 'user_data'
                const storedUserData = localStorage.getItem('user_data');
                if (storedUserData) {
                  const userData = JSON.parse(storedUserData);
                  if (userData.tipoPlano) {
                    user.tipoPlano = userData.tipoPlano;
                    
                  }
                }
                
                // 2. Verificar dados offline do usuário se não encontrou acima
                if (!user.tipoPlano && user.username) {
                  const offlineUserData = localStorage.getItem(`offline_user_${user.username}`);
                  if (offlineUserData) {
                    const parsedOfflineData = JSON.parse(offlineUserData);
                    if (parsedOfflineData.tipoPlano) {
                      user.tipoPlano = parsedOfflineData.tipoPlano;
                      
                    } else if (parsedOfflineData.user && parsedOfflineData.user.tipoPlano) {
                      user.tipoPlano = parsedOfflineData.user.tipoPlano;
                      
                    }
                  }
                }
              } catch (error) {
                
              }
            }
            
            // Importante: atualizar o localStorage com os dados completos
            if (user.tipoPlano) {
              
              localStorage.setItem('user_data', JSON.stringify(user));
            }

            // Verificar role do usuário
            if (!checkUserRole(user)) {
              return;
            }
            
            setCurrentUser(user);
          }
        }
      } catch (error) {
        
        // Em caso de erro, limpar dados e redirecionar para login
        apiService.logout();
        setCurrentUser(null);
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [navigate]);

  // Função para fazer login
  const login = async (username, password) => {
    try {
      const result = await apiService.login(username, password);
      console.log('Resultado do login (completo):', JSON.stringify(result, null, 2)); // Log detalhado do resultado
      console.log('Status emailVerificado no login:', result.user?.emailVerificado); // Log específico do emailVerificado

      if (result.success) {
        const user = result.user;
        console.log('Dados do usuário após login (completo):', JSON.stringify(user, null, 2)); // Log detalhado do usuário

        // Verificar se o tipoPlano está presente
        let tipoPlano = user.tipoPlano;
        
        // Se não tiver tipoPlano, tentar recuperar de outras fontes
        if (!tipoPlano) {
          
          
          try {
            // Verificar no localStorage
            const storedUserData = localStorage.getItem('user_data');
            if (storedUserData) {
              const userData = JSON.parse(storedUserData);
              if (userData.tipoPlano) {
                tipoPlano = userData.tipoPlano;
                
              }
            }
            
            // Verificar dados offline específicos deste usuário
            const offlineUserData = localStorage.getItem(`offline_user_${username}`);
            if (offlineUserData && !tipoPlano) {
              const parsedData = JSON.parse(offlineUserData);
              if (parsedData.tipoPlano) {
                tipoPlano = parsedData.tipoPlano;
                
              } else if (parsedData.user && parsedData.user.tipoPlano) {
                tipoPlano = parsedData.user.tipoPlano;
                
              }
            }
          } catch (error) {
            
          }
        } else {
          
        }
        
        // Garantir que o tipoPlano esteja no objeto do usuário
        result.user.tipoPlano = tipoPlano;
        console.log('Dados finais do usuário após processamento:', result.user); // Log dos dados finais

        // Tentar extrair a empresa do email
        if (result.user && result.user.email) {
          const empresaDoEmail = extrairNomeEmpresa(result.user.email);
          if (empresaDoEmail) {
            result.user.empresa = normalizeEmpresa(empresaDoEmail);
            
            // Atualizar a empresa no serviço API
            try {
              await apiService.updateUserEmpresa(result.user.id, result.user.empresa);
            } catch (error) {
              
              // Não interromper o fluxo se falhar a atualização da empresa
            }
          } else if (result.user.empresa) {
            // Se não conseguir extrair do email, normalizar a empresa existente
            result.user.empresa = normalizeEmpresa(result.user.empresa);
          }
        }
        
        // Adicionar propriedade isOffline se estiver em modo offline
        if (result.isOffline) {
          result.user.isOffline = true;
        }

        // Verificar role do usuário antes de definir o currentUser
        if (result.user.role === 'inativo') {
          
          // Não limpar os dados do usuário aqui, apenas retornar o status
          return { 
            success: true, 
            isOffline: result.isOffline || false,
            user: result.user,
            isInactive: true
          };
        }
        
        // Atualizar localStorage com os dados completos, incluindo o tipoPlano
        localStorage.setItem('user_data', JSON.stringify(result.user));
        
        setCurrentUser(result.user);
        console.log('Current user definido no contexto:', result.user); // Log do current user no contexto
        return { 
          success: true, 
          isOffline: result.isOffline || false,
          user: result.user
        };
      }
      
      return { 
        success: false, 
        error: result.error || 'Falha ao fazer login'
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Ocorreu um erro ao fazer login. Tente novamente.'
      };
    }
  };

  // Função para fazer logout
  const logout = () => {
    apiService.logout();
    setCurrentUser(null);
  };

  // Valor do contexto
  const value = {
    currentUser,
    isAuthenticated: !!currentUser && currentUser.role !== 'inativo',
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 